import { getCountryName } from '@toasttab/buffet-pui-phone-utilities'
import { getLocale } from '@toasttab/buffet-pui-locale-utilities'

// Which container is rendering a shared filter. The two sites prefix their row
// ids differently ('filter-…' in the bar, 'panel-filter-…' in the panel); the
// shared components own the rest of the id, so callers pass only the variant.
export type FilterVariant = 'bar' | 'panel'

const ID_PREFIX: Record<FilterVariant, string> = {
  bar: 'filter',
  panel: 'panel-filter'
}

// Composes a shared filter's row-name/id prefix from the container variant and
// the filter's own suffix (e.g. 'tags', 'brand', an option/attribute name).
export const filterName = (variant: FilterVariant, suffix: string): string =>
  `${ID_PREFIX[variant]}-${suffix}`

type IdOrName = { id?: string | null; name?: string | null }

export const matchByIdOrName = (a: IdOrName, b: IdOrName): boolean =>
  a.id && b.id ? a.id === b.id : a.name === b.name

// Cache resolved names (Buffet's getCountryName builds two Intl.DisplayNames per call) keyed by locale, since a locale change alters both the name and the sort order.
let displayNameCacheLocale: string | null = null
const displayNameCache = new Map<string, string>()

export const getCountryDisplayName = (isoCode: string): string => {
  const locale = getLocale()
  if (locale !== displayNameCacheLocale) {
    displayNameCache.clear()
    displayNameCacheLocale = locale
  }
  const cached = displayNameCache.get(isoCode)
  if (cached !== undefined) {
    return cached
  }
  const name = getCountryName(isoCode) ?? isoCode
  displayNameCache.set(isoCode, name)
  return name
}

export const sortCountryCodesByDisplayName = (codes: string[]): string[] => {
  // Pass the guest's locale so collation matches it, not the runtime default.
  const language = getLocale().split('-')[0]
  return [...codes].sort((a, b) =>
    getCountryDisplayName(a).localeCompare(getCountryDisplayName(b), language)
  )
}
